<template>
  <n-card title="Transactions">
    <n-space vertical>
      <!-- Filters -->
      <n-space>
        <n-date-picker v-model:value="filterStart" type="date" placeholder="From" clearable @update:value="reload" />
        <n-date-picker v-model:value="filterEnd"  type="date" placeholder="To"   clearable @update:value="reload" />
        <n-button @click="resetFilters">Reset</n-button>
        <n-button type="primary" @click="openAdd">+ Add Transaction</n-button>
      </n-space>

      <!-- Table -->
      <n-table :bordered="false" :single-line="false">
        <thead>
          <tr>
            <th>Date</th>
            <th>Merchant</th>
            <th>Category</th>
            <th>Target</th>
            <th>Total</th>
            <th>Lines</th>
            <th>Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="tx in transactions" :key="tx.id">
            <tr>
              <td>{{ formatDate(tx.date) }}</td>
              <td>{{ tx.merchant }}</td>
              <td>
                <n-tag v-if="tx.category" size="small" :color="getCategoryColor(tx.category)">
                  {{ tx.category }}
                </n-tag>
                <span v-else style="color:#666">—</span>
              </td>
              <td>
                <n-tag size="small" :type="tx.account_type_target === 'Individual' ? 'primary' : 'error'">
                  {{ tx.account_type_target }}
                </n-tag>
              </td>
              <td>{{ formatCurrency(tx.total_amount) }}</td>
              <td>
                <n-button
                  v-if="tx.line_items?.length"
                  text
                  size="small"
                  @click="toggleLines(tx.id)"
                >
                  {{ expandedId === tx.id ? '▲' : '▼' }} {{ tx.line_items.length }} item(s)
                </n-button>
                <span v-else style="color:#666">—</span>
              </td>
              <td style="max-width:180px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis">
                {{ tx.notes || '—' }}
              </td>
              <td>
                <n-space>
                  <n-button size="small" @click="editTransaction(tx)">Edit</n-button>
                  <n-button size="small" type="error" @click="deleteTransaction(tx.id)">Delete</n-button>
                </n-space>
              </td>
            </tr>
            <!-- Expanded line items -->
            <tr v-if="expandedId === tx.id && tx.line_items?.length">
              <td colspan="8" style="padding: 0 0 8px 32px; background: #101014">
                <n-table size="small" :bordered="false">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Unit price</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in tx.line_items" :key="item.id">
                      <td>{{ item.description }}</td>
                      <td>{{ item.quantity }}</td>
                      <td>{{ formatCurrency(item.unit_price) }}</td>
                      <td>{{ formatCurrency(item.unit_price * item.quantity) }}</td>
                    </tr>
                  </tbody>
                </n-table>
              </td>
            </tr>
          </template>
        </tbody>
      </n-table>

      <!-- Modal -->
      <n-modal
        v-model:show="showForm"
        preset="card"
        :mask-closable="true"
        :title="isEditing ? 'Edit Transaction' : 'Add Transaction'"
        style="width: 680px; max-width: 95vw"
      >
        <n-form ref="formRef" :model="form" label-placement="left" label-width="130">
          <!-- Header fields -->
          <n-form-item label="Date" path="date">
            <n-date-picker v-model:value="form.date" type="date" style="width:100%" />
          </n-form-item>
          <n-form-item label="Merchant" path="merchant">
            <n-input v-model:value="form.merchant" />
          </n-form-item>
          <n-form-item label="Category" path="category">
            <n-select v-model:value="form.category" :options="categoryOptions" clearable />
          </n-form-item>
          <n-form-item label="Account type" path="account_type_target">
            <n-select v-model:value="form.account_type_target" :options="targetOptions" />
          </n-form-item>
          <n-form-item label="Total (€)" path="total_amount">
            <n-input-number v-model:value="form.total_amount" :min="0" :precision="2" style="width:100%" />
          </n-form-item>
          <n-form-item label="Notes" path="notes">
            <n-input v-model:value="form.notes" type="textarea" :rows="2" />
          </n-form-item>

          <!-- Line items -->
          <n-divider>Line items (optional)</n-divider>
          <div v-for="(item, idx) in form.line_items" :key="idx" style="margin-bottom: 8px">
            <n-space align="center">
              <n-input
                v-model:value="item.description"
                placeholder="Description"
                style="width: 220px"
              />
              <n-input-number
                v-model:value="item.quantity"
                placeholder="Qty"
                :min="0.001"
                :precision="3"
                style="width: 80px"
              />
              <n-input-number
                v-model:value="item.unit_price"
                placeholder="Unit price"
                :min="0"
                :precision="2"
                style="width: 100px"
              />
              <n-button size="small" type="error" text @click="removeItem(idx)">✕</n-button>
            </n-space>
          </div>
          <n-button dashed size="small" @click="addItem">+ Add line</n-button>

          <div style="text-align: right; margin-top: 16px">
            <n-button type="primary" @click="submitForm">
              {{ isEditing ? 'Update' : 'Add' }}
            </n-button>
          </div>
        </n-form>
      </n-modal>
    </n-space>
  </n-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useTransactionStore } from '../stores/transactionStore';

const store = useTransactionStore();
const transactions = computed(() => store.transactions);

const showForm = ref(false);
const isEditing = ref(false);
const editingId = ref(null);
const expandedId = ref(null);

const now = new Date();
const filterStart = ref(new Date(now.getFullYear(), now.getMonth(), 1).getTime());
const filterEnd   = ref(new Date(now.getFullYear(), now.getMonth() + 1, 0).getTime());

const toIso = (ts) => ts ? new Date(ts).toISOString().split('T')[0] : null;

const reload = () => store.fetchTransactions(toIso(filterStart.value), toIso(filterEnd.value));
const resetFilters = () => {
  filterStart.value = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  filterEnd.value   = new Date(now.getFullYear(), now.getMonth() + 1, 0).getTime();
  reload();
};

onMounted(reload);

const emptyForm = () => ({
  date: Date.now(),
  merchant: '',
  category: null,
  account_type_target: 'Individual',
  total_amount: 0,
  notes: '',
  line_items: [],
});

const form = ref(emptyForm());

const categoryOptions = [
  { label: 'Alimentation', value: 'Alimentation' },
  { label: 'Transport',    value: 'Transport'    },
  { label: 'Logement',     value: 'Logement'     },
  { label: 'Santé',        value: 'Santé'        },
  { label: 'Loisirs',      value: 'Loisirs'      },
  { label: 'Abonnements',  value: 'Abonnements'  },
  { label: 'Maison',       value: 'Maison'       },
  { label: 'Autre',        value: 'Autre'        },
];

const targetOptions = [
  { label: 'Individual', value: 'Individual' },
  { label: 'Shared',     value: 'Shared'     },
];

const CATEGORY_COLORS = {
  Alimentation: { color: '#3b8c5a', textColor: '#fff', borderColor: '#3b8c5a' },
  Transport:    { color: '#2563eb', textColor: '#fff', borderColor: '#2563eb' },
  Logement:     { color: '#7c3aed', textColor: '#fff', borderColor: '#7c3aed' },
  'Santé':      { color: '#dc2626', textColor: '#fff', borderColor: '#dc2626' },
  Loisirs:      { color: '#d97706', textColor: '#fff', borderColor: '#d97706' },
  Abonnements:  { color: '#0891b2', textColor: '#fff', borderColor: '#0891b2' },
  Maison:       { color: '#b45309', textColor: '#fff', borderColor: '#b45309' },
  Autre:        { color: '#6b7280', textColor: '#fff', borderColor: '#6b7280' },
};
const getCategoryColor = (c) => CATEGORY_COLORS[c] ?? CATEGORY_COLORS.Autre;

const toggleLines = (id) => { expandedId.value = expandedId.value === id ? null : id; };

const openAdd = () => {
  isEditing.value = false;
  editingId.value = null;
  form.value = emptyForm();
  showForm.value = true;
};

const editTransaction = (tx) => {
  isEditing.value = true;
  editingId.value = tx.id;
  form.value = {
    ...tx,
    date: new Date(tx.date).getTime(),
    line_items: (tx.line_items ?? []).map(i => ({
      description: i.description,
      quantity: i.quantity,
      unit_price: i.unit_price,
    })),
  };
  showForm.value = true;
};

const addItem    = () => form.value.line_items.push({ description: '', quantity: 1, unit_price: 0 });
const removeItem = (idx) => form.value.line_items.splice(idx, 1);

const submitForm = async () => {
  try {
    if (isEditing.value) {
      await store.updateTransaction(editingId.value, form.value);
    } else {
      await store.addTransaction(form.value);
    }
    showForm.value = false;
  } catch (e) {
    console.error('Error submitting transaction:', e);
  }
};

const deleteTransaction = async (id) => {
  try { await store.deleteTransaction(id); }
  catch (e) { console.error('Error deleting transaction:', e); }
};

const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR');
const formatCurrency = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);
</script>
